import React from 'react'
import { Navigate, useLocation, useRoutes, useParams, RouteObject } from 'react-router-dom'

import { RouteGuard, Spinner, Text } from 'components'
import NavigateWithSearch from 'components/Route/NavigateWithSearch'
import { FETCHING_STATUS } from 'utils/constants'
import usePostLoginSetup from 'hooks/use-post-login-setup'
import useUserPermissions from 'hooks/use-user-permissions'
import useAuthenticate from 'hooks/use-authenticate'
import useBranchFeature from 'hooks/use-branch-feature'
import PageNotFound, { PageNotFoundWithoutNavBar } from 'modules/PageNotFound'
import { ScopeModalProvider } from 'hooks/use-scope-modal'
import { INITIAL_PAGE } from 'utils/constants/url'
import { useAppSelector } from 'store'
import { useCampaignDetail } from 'queries/lead-campaign'
import { ErrorView } from 'components'
import { FlexCenter } from 'modules/Settings/shared/styles'
import { PRODUCT_PRICE_URI } from 'modules/Settings/ProductAndPrice/configs'
import { url as pendingApprovalQuotationUrl } from 'modules/Opportunities/PendingApprovalQuotation'

// Lazy loaded components
const Dashboard = React.lazy(() => import(/* webpackChunkName: "dashboard" */ 'modules/Dashboard'))
// RM Conversion + Lead Stagnation: one chunk (same webpackChunkName) holding the shared layout route and both
// pages, so switching between the two does not fetch another chunk.
const RmDashboardLayout = React.lazy(() =>
  import(/* webpackChunkName: "rmConversionLeadStagnationDashboards" */ 'modules/Dashboard/shared/rmDashboard'),
)
const RmConversionDashboard = React.lazy(() =>
  import(/* webpackChunkName: "rmConversionLeadStagnationDashboards" */ 'modules/Dashboard/RmConversion'),
)
const LeadStagnationDashboard = React.lazy(() =>
  import(/* webpackChunkName: "rmConversionLeadStagnationDashboards" */ 'modules/Dashboard/LeadStagnation'),
)
const RmEvents = React.lazy(() => import(/* webpackChunkName: "rmEvents" */ 'modules/Dashboard/RmEvents'))
// Lead Capture Quality + Agent Follow-Through: one chunk (same webpackChunkName) holding both pages and the
// shared code they use, so switching between the two does not fetch another chunk.
const LeadCaptureQualityDashboard = React.lazy(() =>
  import(/* webpackChunkName: "leadCaptureFollowThroughDashboards" */ 'modules/Dashboard/LeadCaptureQualityDashboard'),
)
const AgentFollowThroughDashboard = React.lazy(() =>
  import(/* webpackChunkName: "leadCaptureFollowThroughDashboards" */ 'modules/Dashboard/AgentFollowThroughDashboard'),
)
const Accounts = React.lazy(() => import(/* webpackChunkName: "accounts" */ 'modules/Accounts'))
const Customers = React.lazy(() => import(/* webpackChunkName: "customers" */ 'modules/Customers'))
const Leads = React.lazy(() => import(/* webpackChunkName: "leads" */ 'modules/Leads'))
const MergeLead = React.lazy(() => import(/* webpackChunkName: "mergeLead" */ 'modules/Leads/MergeLead'))
const Import = React.lazy(() => import(/* webpackChunkName: "import" */ 'modules/Import'))
const ImportHistory = React.lazy(() => import(/* webpackChunkName: "importHistory" */ 'modules/ImportHistory'))
const ImportV2 = React.lazy(() => import(/* webpackChunkName: "importV2" */ 'modules/ImportV2'))
const ImportV2Upload = React.lazy(() => import(/* webpackChunkName: "importV2Upload" */ 'modules/ImportV2/Upload'))
const ImportV2TaskDetail = React.lazy(() =>
  import(/* webpackChunkName: "importV2TaskDetail" */ 'modules/ImportV2/TaskDetail'),
)
const LeadDetailV2 = React.lazy(() => import(/* webpackChunkName: "lead.details.v2" */ 'modules/Leads/LeadDetailV2'))
const NewLead = React.lazy(() =>
  import(/* webpackPrefetch: true */ /* webpackChunkName: "newLead" */ 'modules/Leads/NewLead'),
)
const Opportunities = React.lazy(() => import(/* webpackChunkName: "opportunities" */ 'modules/Opportunities'))
const Settings = React.lazy(() => import(/* webpackChunkName: "settings" */ 'modules/Settings'))
const Tasks = React.lazy(() => import(/* webpackChunkName: "tasks" */ 'modules/Tasks'))
const Report = React.lazy(() => import(/* webpackChunkName: "report" */ 'modules/Report'))
const Calendar = React.lazy(() => import(/* webpackChunkName: "calendar" */ 'modules/Calendar'))
const BulkUpdateLeadsForm = React.lazy(() =>
  import(/* webpackChunkName: "bulkUpdateLeadsForm" */ 'modules/BulkUpdateLeads/Form'),
)
const BulkUpdateLeadsHistory = React.lazy(() =>
  import(/* webpackChunkName: "bulkUpdateLeadsHistory" */ 'modules/BulkUpdateLeads/History'),
)
const PendingApprovalQuotation = React.lazy(() =>
  import(/* webpackChunkName: "pendingApprovalQuotation" */ 'modules/Opportunities/PendingApprovalQuotation'),
)
const Welcome = React.lazy(() => import('modules/Auth/Welcome'))
const Verification = React.lazy(() => import('modules/Auth/Verification'))
const Login = React.lazy(() => import('modules/Auth/Login'))
const ForgotPassword = React.lazy(() => import('modules/Auth/ForgotPassword'))
const TwoFactorVerification = React.lazy(() => import('modules/Auth/TwoFactorVerification'))

// Settings components
const Profile = React.lazy(() => import('modules/Settings/Profile'))
const CompanyProfile = React.lazy(() => import('modules/Settings/CompanyProfile'))
const Permission = React.lazy(() => import('modules/Settings/Permission'))
const BranchSettings = React.lazy(() => import('modules/Settings/Workflow/Quotation'))
const ChangePassword = React.lazy(() => import('modules/Settings/ChangePassword'))
const Approvers = React.lazy(() => import('modules/Settings/Approvers'))
const LeadStatuses = React.lazy(() => import('modules/Settings/Workflow/Leads/LeadStatuses'))
const CustomData = React.lazy(() => import('modules/Settings/Workflow/CustomFields/CustomData'))
const CustomLeadForm = React.lazy(() => import('modules/Settings/Workflow/CustomFields/CustomLeadForm'))
const CustomOpportunityForm = React.lazy(() => import('modules/Settings/Workflow/CustomFields/CustomOpportunityForm'))
const General = React.lazy(() => import('modules/Settings/Workflow/General'))
const ImportFitments = React.lazy(() =>
  import('modules/Settings/Cams/ImportFitments').then((module) => ({ default: module.default ?? module })),
)
const EmailSignature = React.lazy(() => import('modules/Settings/EmailSignature'))
const UserActivityAuditLog = React.lazy(() =>
  import('modules/Settings/UserActivityAuditLog').then((module: any) => ({ default: module.default ?? module })),
)

// Settings nested components
const UserList = React.lazy(() =>
  import('modules/Settings/Users/UserList').then((module) => ({ default: module.default ?? module })),
)
const UserDetail = React.lazy(() => import('modules/Settings/Users/UserDetail'))
const TeamsList = React.lazy(() => import('modules/Settings/Teams/TeamsList'))
const TeamDetails = React.lazy(() => import('modules/Settings/Teams/TeamDetails'))
const TeamForm = React.lazy(() => import('modules/Settings/Teams/TeamForm'))
const LeadSourceTypeList = React.lazy(() => import('modules/Settings/Campaign/LeadSourceType/LeasSourceTypeList'))
const LeadSourceType = React.lazy(() => import('modules/Settings/Campaign/LeadSourceType/LeadSourceType'))
const LeadSource = React.lazy(() => import('modules/Settings/Campaign/LeadSource/LeadSource'))
const LeadSourceList = React.lazy(() => import('modules/Settings/Campaign/LeadSource/LeadSourceList'))
const CampaignForm = React.lazy(() => import('modules/Settings/Campaign/LeadCampaign/Form'))
const CampaignInfo = React.lazy(() => import('modules/Settings/Campaign/LeadCampaign/Info'))
const CampaignList = React.lazy(() => import('modules/Settings/Campaign/LeadCampaign/List'))
const SalesObjectiveForm = React.lazy(() => import('modules/Settings/Campaign/SalesObjective/Form'))
const SalesObjectiveInfo = React.lazy(() => import('modules/Settings/Campaign/SalesObjective/Info'))
const SalesObjectiveList = React.lazy(() => import('modules/Settings/Campaign/SalesObjective/List'))
const Customization = React.lazy(() => import('modules/Settings/Workflow/Customization'))
const SalesPipeline = React.lazy(() => import('modules/Settings/Workflow/Leads/SalesPipeline'))
const SalesPipelineDetails = React.lazy(() =>
  import('modules/Settings/Workflow/Leads/SalesPipeline/SalesPipelineDetails'),
)
const NewSalesPipeline = React.lazy(() =>
  import('modules/Settings/Workflow/Leads/SalesPipeline/SalesPipelineDetails/NewSalesPipeline'),
)
const ProductGroup = React.lazy(() => import('modules/Settings/ProductAndPrice/ProductGroup'))
const Bundle = React.lazy(() => import('modules/Settings/ProductAndPrice/Bundle'))
const Product = React.lazy(() => import('modules/Settings/ProductAndPrice/Product'))
const Service = React.lazy(() => import('modules/Settings/ProductAndPrice/Service'))
const Addon = React.lazy(() => import('modules/Settings/ProductAndPrice/Addon'))
const Price = React.lazy(() => import('modules/Settings/ProductAndPrice/Price'))
const ProductCustomField = React.lazy(() => import('modules/Settings/ProductAndPrice/CustomField'))
const PitchLibrary = React.lazy(() => import(/* webpackChunkName: "pitchLibrary" */ 'modules/Settings/PitchLibrary'))

import LeadDetailsWrapper from 'modules/Leads/LeadDetailsWrapper'
import SuspenseLoader from 'modules/App/SuspenseLoader'
import { useTeamDetail } from 'queries/team'
import { ExternalApiDocumentation, ExternalApiKeys } from 'modules/Settings/ExternalApi'
import { ROUTES } from './constants/constants'
import { ROUTE_BASE } from './constants/constants'

const LeadDetailV2Component = React.lazy(() =>
  import('modules/Leads/LeadDetailV2').then((module) => ({
    default: module.LeadDetailV2Component,
  })),
)

// Suspense wrapper component
const SuspenseWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <SuspenseLoader>{children}</SuspenseLoader>
)

const AuthRoutes = () => {
  const { isAuthenticated } = useAuthenticate()

  const routes: RouteObject[] = React.useMemo(() => {
    if (isAuthenticated) {
      return [
        {
          path: '*',
          element: <Navigate to={ROUTES.HOME} />,
        },
      ]
    }

    return [
      {
        path: ROUTES.LOGIN,
        element: (
          <RouteGuard requiresAuthentication={false} withErrorBoundary={false}>
            <SuspenseWrapper>
              <Login />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.TWO_FACTOR_VERIFICATION,
        element: (
          <RouteGuard requiresAuthentication={false} withErrorBoundary={false}>
            <SuspenseWrapper>
              <TwoFactorVerification />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.FORGOT_PASSWORD,
        element: (
          <RouteGuard requiresAuthentication={false} withErrorBoundary={false}>
            <SuspenseWrapper>
              <ForgotPassword />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: '*',
        element: <Navigate to={ROUTES.LOGIN} />,
      },
    ]
  }, [isAuthenticated])

  return useRoutes(routes)
}

const PostLoginRoutes: React.FC = () => {
  const location = useLocation()
  const { isAuthenticated, isSelectedBranch } = useAuthenticate()
  usePostLoginSetup()

  const [accountWorkflow] = useUserPermissions('accountWorkflow')
  const [dataWorkflow] = useUserPermissions('dataWorkflow')
  const [reports] = useUserPermissions('reports')

  const currentPath = location.pathname
  const nextUrl = currentPath !== `/${ROUTES.WELCOME}` && currentPath !== ROUTES.HOME ? currentPath : null

  const routes: RouteObject[] = React.useMemo(() => {
    if (!isAuthenticated) {
      return [
        {
          path: '*',
          element: <Navigate to={`/${ROUTE_BASE.AUTH}/${ROUTES.LOGIN}${nextUrl ? `?nextUrl=${nextUrl}` : ''}`} />,
        },
      ]
    }

    if (!isSelectedBranch) {
      return [
        {
          path: '*',
          element: <Navigate to={`/${ROUTES.WELCOME}${nextUrl ? `?nextUrl=${nextUrl}` : ''}`} />,
        },
      ]
    }

    return [
      {
        path: ROUTES.DASHBOARD,
        element: (
          <SuspenseWrapper>
            <RouteGuard>
              <Dashboard />
            </RouteGuard>
          </SuspenseWrapper>
        ),
      },
      {
        // Pathless layout route: the filter bar stays mounted while switching between the two pages.
        element: (
          <SuspenseWrapper>
            <RouteGuard>
              <RmDashboardLayout />
            </RouteGuard>
          </SuspenseWrapper>
        ),
        children: [
          { path: ROUTES.RM_CONVERSION, element: <RmConversionDashboard /> },
          { path: ROUTES.LEAD_STAGNATION, element: <LeadStagnationDashboard /> },
        ],
      },
      {
        // Legacy combined dashboard, split into RM Conversion and Lead Stagnation.
        path: ROUTES.RM_CONVERSION_STAGNATION,
        element: <NavigateWithSearch to={`/${ROUTES.RM_CONVERSION}`} />,
      },
      {
        path: ROUTES.RM_EVENTS,
        element: (
          <SuspenseWrapper>
            <RouteGuard>
              <RmEvents />
            </RouteGuard>
          </SuspenseWrapper>
        ),
      },
      {
        path: ROUTES.LEAD_CAPTURE_QUALITY_DASHBOARD,
        element: (
          <SuspenseWrapper>
            <RouteGuard>
              <LeadCaptureQualityDashboard />
            </RouteGuard>
          </SuspenseWrapper>
        ),
      },
      {
        path: ROUTES.AGENT_FOLLOW_THROUGH_DASHBOARD,
        element: (
          <SuspenseWrapper>
            <RouteGuard>
              <AgentFollowThroughDashboard />
            </RouteGuard>
          </SuspenseWrapper>
        ),
      },
      {
        // Legacy combined dashboard, split into Lead Capture Quality and Agent Follow-Through.
        path: ROUTES.LEAD_HYGIENE_DASHBOARD,
        element: <NavigateWithSearch to={`/${ROUTES.LEAD_CAPTURE_QUALITY_DASHBOARD}`} />,
      },
      {
        path: ROUTES.MERGE_LEAD,
        element: (
          <SuspenseWrapper>
            <RouteGuard>
              <MergeLead />
            </RouteGuard>
          </SuspenseWrapper>
        ),
      },
      {
        path: ROUTES.PREVIEW_LEAD,
        element: (
          <SuspenseWrapper>
            <RouteGuard>
              <LeadDetailV2Component height="100vh" />
            </RouteGuard>
          </SuspenseWrapper>
        ),
      },
      {
        path: ROUTES.LEAD_DETAILS_V2,
        element: (
          <SuspenseWrapper>
            <RouteGuard>
              <LeadDetailV2 />
            </RouteGuard>
          </SuspenseWrapper>
        ),
      },
      {
        path: ROUTES.LEAD_DETAILS,
        element: (
          <RouteGuard>
            <LeadDetailsWrapper />
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.LEADS,
        element: (
          <SuspenseWrapper>
            <RouteGuard>
              <Leads />
            </RouteGuard>
          </SuspenseWrapper>
        ),
      },
      {
        path: ROUTES.LEADS_IMPORT,
        element: (
          <RouteGuard withPermission={dataWorkflow.canImport}>
            <SuspenseWrapper>
              <Import />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.LEADS_HISTORY,
        element: (
          <RouteGuard withPermission={dataWorkflow.canImport}>
            <SuspenseWrapper>
              <ImportHistory />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.IMPORTS_V2,
        element: (
          <RouteGuard withPermission={dataWorkflow.canImport}>
            <SuspenseWrapper>
              <ImportV2 />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.IMPORTS_V2_NEW,
        element: (
          <RouteGuard withPermission={dataWorkflow.canImport}>
            <SuspenseWrapper>
              <ImportV2Upload />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.IMPORTS_V2_TASK,
        element: (
          <RouteGuard withPermission={dataWorkflow.canImport}>
            <SuspenseWrapper>
              <ImportV2TaskDetail />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.LEADS_BULK_UPDATE,
        element: (
          <RouteGuard>
            <BulkUpdateLeadsForm />
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.LEADS_BULK_UPDATE_HISTORY,
        element: (
          <RouteGuard>
            <BulkUpdateLeadsHistory />
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.REPORTS,
        element: (
          <RouteGuard withPermission={reports.canView}>
            <SuspenseWrapper>
              <Report />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.NEW_LEAD,
        element: (
          <RouteGuard withPermission={accountWorkflow.canCreate}>
            <SuspenseWrapper>
              <ScopeModalProvider>
                <NewLead />
              </ScopeModalProvider>
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.TASKS,
        element: (
          <RouteGuard>
            <SuspenseWrapper>
              <Tasks />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.ACCOUNTS,
        element: (
          <RouteGuard>
            <SuspenseWrapper>
              <Accounts />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.OPPORTUNITIES,
        element: (
          <RouteGuard>
            <SuspenseWrapper>
              <Opportunities />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: pendingApprovalQuotationUrl.replace(/^\//, ''),
        element: (
          <RouteGuard>
            <SuspenseWrapper>
              <PendingApprovalQuotation />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.CUSTOMERS,
        element: (
          <RouteGuard>
            <SuspenseWrapper>
              <Customers />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.SETTINGS,
        element: (
          <RouteGuard withErrorBoundary={false}>
            <SuspenseWrapper>
              <Settings />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.CALENDAR,
        element: (
          <RouteGuard>
            <SuspenseWrapper>
              <Calendar />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: '*',
        element: (
          <RouteGuard>
            <PageNotFoundWithoutNavBar isTokenPresent={false} />
          </RouteGuard>
        ),
      },
    ]
  }, [isAuthenticated, isSelectedBranch, nextUrl, accountWorkflow.canCreate, dataWorkflow.canImport, reports.canView])

  return useRoutes(routes)
}

const UserRoutes = () => {
  const routes: RouteObject[] = React.useMemo(
    () => [
      {
        index: true,
        element: (
          <RouteGuard withErrorBoundary={false}>
            <SuspenseWrapper>
              <UserList />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.USER_DETAILS,
        element: (
          <RouteGuard redirectTo={`${ROUTES.SETTINGS}/${ROUTES.PROFILE}`} withErrorBoundary={false}>
            <SuspenseWrapper>
              <UserDetail />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: '*',
        element: <Navigate to={`${ROUTES.SETTINGS}/${ROUTES.USERS}`} replace />,
      },
    ],
    [],
  )

  return useRoutes(routes)
}

const TeamDetailRoutes = ({ ...props }: { permissions: { canView: boolean; canEdit: boolean } }) => {
  const { teamId } = useParams<{ teamId: string }>()
  const { data: team, isLoading, isError } = useTeamDetail(teamId ?? null)

  const routes: RouteObject[] = React.useMemo(
    () => [
      {
        index: true,
        element: (
          <RouteGuard withPermission={props.permissions.canView}>
            <SuspenseWrapper>
              <TeamDetails team={team} isLoading={isLoading} isError={isError} />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.TEAM_EDIT,
        element: <RouteGuard withPermission={props.permissions.canEdit}>{team && <TeamForm data={team} />}</RouteGuard>,
      },
    ],
    [props.permissions, team, isLoading, isError],
  )

  return useRoutes(routes)
}

// Team Routes Component
const TeamRoutes = () => {
  const [{ canView, canEdit }, fetchPermissionStatus] = useUserPermissions('userGroup')

  const routes: RouteObject[] = React.useMemo(() => {
    if (fetchPermissionStatus === FETCHING_STATUS.unSet || fetchPermissionStatus === FETCHING_STATUS.loading) {
      return [
        {
          path: '*',
          element: (
            <section>
              <Spinner />
            </section>
          ),
        },
      ]
    }

    if (fetchPermissionStatus === FETCHING_STATUS.fail) {
      return [
        {
          path: '*',
          element: (
            <section>
              <Text>Something went wrong.</Text>
            </section>
          ),
        },
      ]
    }

    return [
      {
        index: true,
        element: (
          <RouteGuard withErrorBoundary={false} withPermission={canView}>
            <SuspenseWrapper>
              <TeamsList />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.TEAM_CREATE,
        element: (
          <RouteGuard withErrorBoundary={false} withPermission={canEdit}>
            <SuspenseWrapper>
              <TeamForm />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.TEAM_DETAILS,
        element: (
          <RouteGuard withErrorBoundary={false} withPermission={!!canView}>
            <TeamDetailRoutes permissions={{ canEdit: !!canEdit, canView: !!canView }} />
          </RouteGuard>
        ),
      },
      {
        path: '*',
        element: <Navigate to={`/${ROUTE_BASE.SETTINGS}/${ROUTE_BASE.TEAMS}`} replace />,
      },
    ]
  }, [fetchPermissionStatus, canView, canEdit])

  return useRoutes(routes)
}

// Lead Source Type Routes
const LeadSourceTypeRoutes = () => {
  const [{ canView }, fetchPermissionStatus] = useUserPermissions('leadSourceType')

  const routes: RouteObject[] = React.useMemo(() => {
    if (fetchPermissionStatus === FETCHING_STATUS.unSet || fetchPermissionStatus === FETCHING_STATUS.loading) {
      return [
        {
          path: '*',
          element: (
            <section>
              <Spinner />
            </section>
          ),
        },
      ]
    }

    if (fetchPermissionStatus === FETCHING_STATUS.fail) {
      return [
        {
          path: '*',
          element: (
            <section>
              <Text>Something went wrong.</Text>
            </section>
          ),
        },
      ]
    }

    return [
      {
        index: true,
        element: (
          <RouteGuard withErrorBoundary={false} withPermission={canView}>
            <SuspenseWrapper>
              <LeadSourceTypeList />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.LEAD_SOURCE_TYPE_DETAILS,
        element: (
          <RouteGuard withErrorBoundary={false} withPermission={canView}>
            <SuspenseWrapper>
              <LeadSourceType />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: '*',
        element: <Navigate to={`/${ROUTE_BASE.SETTINGS}/${ROUTE_BASE.LEAD_SOURCE_TYPES}`} replace />,
      },
    ]
  }, [fetchPermissionStatus, canView])

  return useRoutes(routes)
}

// Lead Source Routes
const LeadSourceRoutes = () => {
  const [{ canView }, fetchPermissionStatus] = useUserPermissions('leadSourceType')

  const routes: RouteObject[] = React.useMemo(() => {
    if (fetchPermissionStatus === FETCHING_STATUS.unSet || fetchPermissionStatus === FETCHING_STATUS.loading) {
      return [
        {
          path: '*',
          element: (
            <section>
              <Spinner />
            </section>
          ),
        },
      ]
    }

    if (fetchPermissionStatus === FETCHING_STATUS.fail) {
      return [
        {
          path: '*',
          element: (
            <section>
              <Text>Something went wrong.</Text>
            </section>
          ),
        },
      ]
    }

    return [
      {
        index: true,
        element: (
          <RouteGuard withErrorBoundary={false} withPermission={true}>
            <SuspenseWrapper>
              <LeadSourceList />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.LEAD_SOURCE_DETAILS,
        element: (
          <RouteGuard withErrorBoundary={false} withPermission={canView}>
            <SuspenseWrapper>
              <LeadSource />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: '*',
        element: <Navigate to={`/${ROUTE_BASE.SETTINGS}/${ROUTE_BASE.LEAD_SOURCES}`} replace />,
      },
    ]
  }, [fetchPermissionStatus, canView])

  return useRoutes(routes)
}

const CampaignInfoRoutes = ({ ...props }: { permissions: { canView: boolean; canEdit: boolean } }) => {
  const { leadCampaignId = '' } = useParams<{ leadCampaignId: string }>()
  const { data, isLoading, isError, error, refetch } = useCampaignDetail(leadCampaignId)

  const routes: RouteObject[] = React.useMemo(() => {
    if (isLoading) {
      return [
        {
          path: '*',
          element: (
            <FlexCenter>
              <Spinner />
            </FlexCenter>
          ),
        },
      ]
    }

    if (isError) {
      return [
        {
          path: '*',
          element: <ErrorView error={error} />,
        },
      ]
    }

    return [
      {
        index: true,
        element: (
          <RouteGuard withPermission={props.permissions.canView} redirectTo="/settings/lead-campaigns">
            <SuspenseWrapper>
              <CampaignInfo src={data} refetch={refetch} />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.CAMPAIGN_EDIT,
        element: (
          <RouteGuard withPermission={props.permissions.canEdit} redirectTo="/settings/lead-campaigns">
            <SuspenseWrapper>
              <CampaignForm src={data} refetch={refetch} />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
    ]
  }, [isLoading, isError, error, props.permissions, data, refetch])

  return useRoutes(routes)
}

// Lead Campaign Routes
const LeadCampaignRoutes = () => {
  const [{ canView, canEdit }, fetchPermissionStatus] = useUserPermissions('leadCampaign')
  const { unSet, loading, fail } = FETCHING_STATUS

  const routes: RouteObject[] = React.useMemo(() => {
    if (fetchPermissionStatus === unSet || fetchPermissionStatus === loading) {
      return [
        {
          path: '*',
          element: (
            <FlexCenter>
              <Spinner />
            </FlexCenter>
          ),
        },
      ]
    }

    if (fetchPermissionStatus === fail) {
      return [
        {
          path: '*',
          element: (
            <FlexCenter>
              <Text>Something went wrong.</Text>
            </FlexCenter>
          ),
        },
      ]
    }

    return [
      {
        index: true,
        element: (
          <RouteGuard withErrorBoundary={false} withPermission={canView}>
            <SuspenseWrapper>
              <CampaignList />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.LEAD_CAMPAIGN_DETAILS,
        element: (
          <RouteGuard withErrorBoundary={false}>
            <CampaignInfoRoutes
              permissions={{
                canEdit: canEdit ?? false,
                canView: canView ?? false,
              }}
            />
          </RouteGuard>
        ),
      },
      {
        path: '*',
        element: <Navigate to={`/${ROUTE_BASE.SETTINGS}/${ROUTE_BASE.LEAD_CAMPAIGNS}`} replace />,
      },
    ]
  }, [fetchPermissionStatus, unSet, loading, fail, canView, canEdit])

  return useRoutes(routes)
}

// Sales Objective Routes
const SalesObjectiveRoutes = () => {
  const [{ canView, canEdit }, fetchPermissionStatus] = useUserPermissions('leadCampaign')
  const { unSet, loading, fail } = FETCHING_STATUS

  const routes: RouteObject[] = React.useMemo(() => {
    if (fetchPermissionStatus === unSet || fetchPermissionStatus === loading) {
      return [
        {
          path: '*',
          element: (
            <FlexCenter>
              <Spinner />
            </FlexCenter>
          ),
        },
      ]
    }

    if (fetchPermissionStatus === fail) {
      return [
        {
          path: '*',
          element: (
            <FlexCenter>
              <Text>request_error</Text>
            </FlexCenter>
          ),
        },
      ]
    }

    return [
      {
        index: true,
        element: (
          <RouteGuard withErrorBoundary={false} withPermission={canView}>
            <SuspenseWrapper>
              <SalesObjectiveList />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.SALES_OBJECTIVE_DETAILS,
        element: (
          <RouteGuard withErrorBoundary={false} withPermission={canView}>
            <SuspenseWrapper>
              <SalesObjectiveInfo />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.SALES_OBJECTIVE_EDIT,
        element: (
          <RouteGuard withErrorBoundary={false} withPermission={canEdit}>
            <SuspenseWrapper>
              <SalesObjectiveForm />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: '*',
        element: <Navigate to={`/${ROUTE_BASE.SETTINGS}/${ROUTES.SALES_OBJECTIVE}`} replace />,
      },
    ]
  }, [fetchPermissionStatus, unSet, loading, fail, canView, canEdit])

  return useRoutes(routes)
}

// Workflow Leads Routes Component
const WorkflowLeadsRoutes = () => {
  const {
    user: { userPermissions },
  } = useAppSelector((state) => state)
  const { pathname } = useLocation()
  const { loading, unSet, fail } = FETCHING_STATUS

  const HeaderComponent = () => {
    if (pathname === ROUTES.LEAD_STATUS_DETAILS || pathname === ROUTES.PIPELINE) {
      return (
        <div style={{ padding: '24px 36px' }}>
          <Text fontWeight="bold" fontSize="22px">
            Leads
          </Text>
        </div>
      )
    }
    return null
  }

  const routes: RouteObject[] = React.useMemo(() => {
    if (userPermissions.status === loading || userPermissions.status === unSet) {
      return [
        {
          path: '*',
          element: (
            <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Spinner />
            </div>
          ),
        },
      ]
    }

    if (userPermissions.status === fail) {
      return [
        {
          path: '*',
          element: <Text>something_went_wrong_error</Text>,
        },
      ]
    }

    const permissions = userPermissions.status === FETCHING_STATUS.success ? userPermissions.data : null

    return [
      {
        path: ROUTES.PIPELINE,
        element: (
          <>
            <HeaderComponent />
            <RouteGuard errorBoundaryWithNavBar={false} withPermission={permissions?.salesPipelines?.canView}>
              <div style={{ padding: '24px 36px' }}>
                <SuspenseWrapper>
                  <SalesPipeline />
                </SuspenseWrapper>
              </div>
            </RouteGuard>
          </>
        ),
      },
      {
        path: ROUTES.PIPELINE_NEW,
        element: (
          <>
            <HeaderComponent />
            <RouteGuard errorBoundaryWithNavBar={false} withPermission={permissions?.salesPipelines?.canView}>
              <SuspenseWrapper>
                <NewSalesPipeline />
              </SuspenseWrapper>
            </RouteGuard>
          </>
        ),
      },
      {
        path: ROUTES.PIPELINE_DETAILS,
        element: (
          <>
            <HeaderComponent />
            <RouteGuard
              errorBoundaryWithNavBar={false}
              withPermission={
                permissions?.salesPipelines?.canEdit ||
                permissions?.automation?.canAnything ||
                permissions?.automation?.canView
              }
            >
              <div style={{ padding: '24px 36px' }}>
                <SuspenseWrapper>
                  <SalesPipelineDetails />
                </SuspenseWrapper>
              </div>
            </RouteGuard>
          </>
        ),
      },
      {
        path: ROUTES.LEAD_STATUS_DETAILS,
        element: (
          <>
            <HeaderComponent />
            <RouteGuard errorBoundaryWithNavBar={false} withPermission={permissions?.leadStatuses?.canView}>
              <div style={{ padding: '24px 36px' }}>
                <SuspenseWrapper>
                  <LeadStatuses />
                </SuspenseWrapper>
              </div>
            </RouteGuard>
          </>
        ),
      },
      {
        path: '*',
        element: <Navigate to={ROUTES.PIPELINE} replace />,
      },
    ]
  }, [userPermissions, pathname, loading, unSet, fail])

  return useRoutes(routes)
}

// Workflow Routes
const WorkflowRoutes = () => {
  const routes: RouteObject[] = React.useMemo(
    () => [
      {
        path: ROUTES.WORKFLOW_SALES,
        element: (
          <RouteGuard errorBoundaryWithNavBar={false}>
            <WorkflowLeadsRoutes />
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.WORKFLOW_CUSTOMIZATION,
        element: (
          <RouteGuard errorBoundaryWithNavBar={false}>
            <SuspenseWrapper>
              <Customization />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: '*',
        element: <Navigate to={`/${ROUTE_BASE.SETTINGS}/${ROUTE_BASE.WORKFLOW}/${ROUTES.CUSTOM_FIELDS}`} replace />,
      },
    ],
    [],
  )

  return useRoutes(routes)
}

// Product and Price Routes
const ProductAndPriceRoutes = () => {
  const branchFeatures = useBranchFeature()

  const routes: RouteObject[] = React.useMemo(() => {
    if (branchFeatures?.quotation === false) {
      return [
        {
          path: '*',
          element: null,
        },
      ]
    }

    return [
      {
        path: ROUTES.PRODUCT_GROUP_DETAILS,
        element: (
          <RouteGuard>
            <SuspenseWrapper>
              <ProductGroup />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.BUNDLE_DETAILS,
        element: (
          <RouteGuard>
            <SuspenseWrapper>
              <Bundle />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.PRODUCT_DETAILS,
        element: (
          <RouteGuard>
            <SuspenseWrapper>
              <Product />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.SERVICE_DETAILS,
        element: (
          <RouteGuard>
            <SuspenseWrapper>
              <Service />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.ADD_ON_DETAILS,
        element: (
          <RouteGuard>
            <SuspenseWrapper>
              <Addon />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.PRICE_DETAILS,
        element: (
          <RouteGuard>
            <SuspenseWrapper>
              <Price />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.CUSTOM_FIELD_DETAILS,
        element: (
          <RouteGuard>
            <SuspenseWrapper>
              <ProductCustomField />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: '*',
        element: <Navigate to="product" replace />,
      },
    ]
  }, [branchFeatures?.quotation])

  return useRoutes(routes)
}

// Settings Routes Component
export const SettingsRoutes = () => {
  const {
    user: { userPermissions },
  } = useAppSelector((state) => state)

  const routes: RouteObject[] = React.useMemo(() => {
    if (userPermissions.status === FETCHING_STATUS.unSet || userPermissions.status === FETCHING_STATUS.loading) {
      return [
        {
          path: '*',
          element: (
            <section>
              <Spinner />
            </section>
          ),
        },
      ]
    }

    if (userPermissions.status === FETCHING_STATUS.fail) {
      return [
        {
          path: '*',
          element: (
            <section>
              <Text>Something went wrong.</Text>
            </section>
          ),
        },
      ]
    }

    const permissions = userPermissions.status === FETCHING_STATUS.success ? userPermissions.data : null

    return [
      {
        path: '/',
        element: <Navigate to="profile" />,
      },
      {
        path: ROUTES.PROFILE,
        element: (
          <RouteGuard errorBoundaryWithNavBar={false}>
            <SuspenseWrapper>
              <Profile />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.PASSWORD,
        element: (
          <RouteGuard redirectTo={`${ROUTES.SETTINGS}/${ROUTES.PROFILE}`} errorBoundaryWithNavBar={false}>
            <SuspenseWrapper>
              <ChangePassword />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.EMAIL_SIGNATURE,
        element: (
          <RouteGuard>
            <SuspenseWrapper>
              <EmailSignature />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.COMPANY_INFO,
        element: (
          <RouteGuard
            redirectTo={`${ROUTES.SETTINGS}/${ROUTES.PROFILE}`}
            errorBoundaryWithNavBar={false}
            withPermission={!!permissions?.company?.canView}
          >
            <SuspenseWrapper>
              <CompanyProfile />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.BRANCHES,
        element: (
          <RouteGuard
            redirectTo={`${ROUTES.SETTINGS}/${ROUTES.PROFILE}`}
            errorBoundaryWithNavBar={false}
            withPermission={!!permissions?.branch?.canView}
          >
            <SuspenseWrapper>
              <Profile />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.USERS,
        element: <UserRoutes />,
      },
      {
        path: ROUTES.TEAMS,
        element: <TeamRoutes />,
      },
      {
        path: ROUTES.APPROVERS,
        element: (
          <RouteGuard
            redirectTo={`${ROUTES.SETTINGS}/${ROUTES.PROFILE}`}
            errorBoundaryWithNavBar={false}
            withPermission={!!permissions?.approvers?.canView}
          >
            <SuspenseWrapper>
              <Approvers />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.USER_ACTIVITY_AUDIT_LOG,
        element: (
          <RouteGuard
            redirectTo={`${ROUTES.SETTINGS}/${ROUTES.PROFILE}`}
            errorBoundaryWithNavBar={false}
            withPermission={!!permissions?.userActivityAudit?.canView}
          >
            <SuspenseWrapper>
              <UserActivityAuditLog />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.GENERAL_WORKFLOW,
        element: (
          <RouteGuard
            redirectTo={`${ROUTES.SETTINGS}/${ROUTES.PROFILE}`}
            errorBoundaryWithNavBar={false}
            withPermission={!!permissions?.customizeLeadType?.canView}
          >
            <SuspenseWrapper>
              <General />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.CUSTOM_FIELDS_CUSTOM_DATA,
        element: (
          <RouteGuard
            redirectTo={`${ROUTES.SETTINGS}/${ROUTES.PROFILE}`}
            errorBoundaryWithNavBar={false}
            withPermission={!!permissions?.customizeLookupList?.canView}
          >
            <SuspenseWrapper>
              <CustomData />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.CUSTOM_FIELDS_CUSTOM_LEAD_FORM,
        element: (
          <RouteGuard
            redirectTo={`${ROUTES.SETTINGS}/${ROUTES.PROFILE}`}
            errorBoundaryWithNavBar={false}
            withPermission={!!permissions?.customizeFormData?.canView}
          >
            <SuspenseWrapper>
              <CustomLeadForm />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.CUSTOM_FIELDS_CUSTOM_OPPORTUNITY_FORM,
        element: (
          <RouteGuard
            redirectTo={`${ROUTES.SETTINGS}/${ROUTES.PROFILE}`}
            errorBoundaryWithNavBar={false}
            withPermission={!!permissions?.customizeFormData?.canView}
          >
            <SuspenseWrapper>
              <CustomOpportunityForm />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.LEAD_STATUSES,
        element: (
          <RouteGuard
            redirectTo={`${ROUTES.SETTINGS}/${ROUTES.PROFILE}`}
            errorBoundaryWithNavBar={false}
            withPermission={!!permissions?.leadStatuses?.canView}
          >
            <SuspenseWrapper>
              <LeadStatuses />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.WORKFLOW,
        element: <WorkflowRoutes />,
      },
      {
        path: ROUTES.LEAD_SOURCE_TYPES,
        element: <LeadSourceTypeRoutes />,
      },
      {
        path: ROUTES.LEAD_SOURCES,
        element: <LeadSourceRoutes />,
      },
      {
        path: ROUTES.LEAD_CAMPAIGNS,
        element: <LeadCampaignRoutes />,
      },
      {
        path: PRODUCT_PRICE_URI,
        element: (
          <RouteGuard
            redirectTo={`${ROUTES.SETTINGS}/${ROUTES.PROFILE}`}
            errorBoundaryWithNavBar={false}
            withPermission={!!permissions?.product?.canView || !!permissions?.price?.canView}
          >
            <ProductAndPriceRoutes />
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.CAMS_IMPORT_FITMENTS,
        element: (
          <RouteGuard
            redirectTo={`${ROUTES.SETTINGS}/${ROUTES.PROFILE}`}
            errorBoundaryWithNavBar={false}
            withPermission={!!permissions?.cams?.canImport}
          >
            <SuspenseWrapper>
              <ImportFitments />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.PERMISSION,
        element: (
          <RouteGuard
            redirectTo={`${ROUTES.SETTINGS}/${ROUTES.PROFILE}`}
            errorBoundaryWithNavBar={false}
            withPermission={!!permissions?.permissions?.canView}
          >
            <SuspenseWrapper>
              <Permission />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.BRANCH_SETTINGS,
        element: (
          <RouteGuard
            redirectTo={`${ROUTES.SETTINGS}/${ROUTES.PROFILE}`}
            errorBoundaryWithNavBar={false}
            withPermission={!!permissions?.branchSettings?.canView}
          >
            <SuspenseWrapper>
              <BranchSettings />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.EXTERNAL_API_KEYS,
        element: (
          <RouteGuard
            redirectTo={`${ROUTES.SETTINGS}/${ROUTES.PROFILE}`}
            withErrorBoundary={false}
            withPermission={!!permissions?.externalApi?.canEdit}
          >
            <SuspenseWrapper>
              <ExternalApiKeys />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.EXTERNAL_API_DOCUMENTATION,
        element: (
          <RouteGuard
            redirectTo={`${ROUTES.SETTINGS}/${ROUTES.PROFILE}`}
            withErrorBoundary={false}
            withPermission={!!permissions?.externalApi?.canView}
          >
            <SuspenseWrapper>
              <ExternalApiDocumentation />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.PITCH_LIBRARY,
        element: (
          <RouteGuard
            redirectTo={`${ROUTES.SETTINGS}/${ROUTES.PROFILE}`}
            withErrorBoundary={false}
            withPermission={!!permissions?.pitchLibrary?.canView}
          >
            <SuspenseWrapper>
              <PitchLibrary />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: `${ROUTES.SALES_OBJECTIVE}/*`,
        element: <SalesObjectiveRoutes />,
      },
      {
        path: '*',
        element: (
          <RouteGuard errorBoundaryWithNavBar={false}>
            <SuspenseWrapper>
              <Profile />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
    ]
  }, [userPermissions])

  return useRoutes(routes)
}

// Main App Routes
const AppRoutes = () => {
  const routes: RouteObject[] = React.useMemo(
    () => [
      {
        path: ROUTES.AUTH,
        element: (
          <RouteGuard requiresAuthentication={false}>
            <AuthRoutes />
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.VERIFICATION,
        element: (
          <RouteGuard requiresAuthentication={false} withErrorBoundary={false}>
            <SuspenseWrapper>
              <Verification />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.WELCOME,
        element: (
          <RouteGuard>
            <SuspenseWrapper>
              <Welcome />
            </SuspenseWrapper>
          </RouteGuard>
        ),
      },
      {
        path: ROUTES.HOME,
        element: <Navigate to={INITIAL_PAGE} />,
      },
      {
        path: '/*',
        element: <PostLoginRoutes />,
      },
      {
        path: '*',
        element: <PageNotFound isTokenPresent={false} />,
      },
    ],
    [],
  )

  return useRoutes(routes)
}

export default AppRoutes
